export const ONBOARDING_FORM_DATA_KEY = 'thera_onboarding_form_data';
export const ONBOARDING_CURRENT_STEP_KEY = 'thera_onboarding_current_step';
export const ONBOARDING_FORM_ADDRESS_DATA_KEY = 'thera_onboarding_form_address_data';
export const ONBOARDING_FORM_BUSINESS_OWNERS_DATA_KEY = 'thera_onboarding_form_business_owners_data';
export const ONBOARDING_FORM_BANKING_TP_DATA_KEY = 'thera_onboarding_form_banking_tp_data';
// The beneficial-owner step's two answers that live outside the owners array: who the control
// person is, and the 25% ownership certification. Both gate Submit, and neither survived a reload.
export const ONBOARDING_OWNERS_CONFIRMATION_KEY = 'thera_onboarding_owners_confirmation';

// Set when a manager clicks "Skip" on the post-onboarding add-payment-method page. Suppresses
// the redirect ladder's gate so subsequent logins don't loop them back. Cleared automatically on
// logout via localStorage.clear() in UserProvider.
export const PAYMENT_METHOD_ONBOARDING_SKIPPED_KEY = 'thera_payment_method_onboarding_skipped';

export const hasSkippedPaymentMethodOnboarding = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(PAYMENT_METHOD_ONBOARDING_SKIPPED_KEY) === 'true';
};

export const markPaymentMethodOnboardingSkipped = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PAYMENT_METHOD_ONBOARDING_SKIPPED_KEY, 'true');
};

// Everything the user has typed into the sign-up wizard. Listed in one place because the wizard
// and the logout path both need to agree on what a draft is; when they drifted, clicking the
// verification email mid-wizard silently wiped the form.
const ONBOARDING_DRAFT_SCOPE_KEY = 'thera_onboarding_draft_scope';

const ONBOARDING_DRAFT_KEYS = [
  ONBOARDING_FORM_DATA_KEY,
  ONBOARDING_CURRENT_STEP_KEY,
  ONBOARDING_FORM_ADDRESS_DATA_KEY,
  ONBOARDING_FORM_BUSINESS_OWNERS_DATA_KEY,
  ONBOARDING_FORM_BANKING_TP_DATA_KEY,
  ONBOARDING_OWNERS_CONFIRMATION_KEY,
  // Snapshotted and restored with the rest, or a preserved draft would come back without its
  // scope and be discarded on the next read.
  ONBOARDING_DRAFT_SCOPE_KEY,
];

export const clearOnboardingDraft = (): void => {
  if (typeof window === 'undefined') return;
  ONBOARDING_DRAFT_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Reads the draft so it can survive a localStorage wipe that is not a real sign-out, such as the
 * token exchange when a user clicks their email-verification link part-way through the wizard.
 */
export const snapshotOnboardingDraft = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  return ONBOARDING_DRAFT_KEYS.reduce<Record<string, string>>((draft, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) draft[key] = value;
    return draft;
  }, {});
};

// The sign-up flow before its company exists. Once the company is created the draft is bound to it, so an admin
// who leaves one unfinished company for another can't carry its answers across.
const SIGN_UP_DRAFT_CONTEXT = 'sign-up';

export const signUpDraftContextFor = (companyId?: string): string =>
  companyId ? `${SIGN_UP_DRAFT_CONTEXT}:${companyId}` : SIGN_UP_DRAFT_CONTEXT;

/**
 * Keeps the draft for this user and flow and points it at a later step, so a wizard that remounts resumes there
 * instead of starting over from the first step.
 */
export const resumeOnboardingDraftAt = (userId: string, context: string, step: number): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ONBOARDING_DRAFT_SCOPE_KEY, `${userId}:${context}`);
  localStorage.setItem(ONBOARDING_CURRENT_STEP_KEY, String(step));
};

export const restoreOnboardingDraft = (draft: Record<string, string>): void => {
  if (typeof window === 'undefined') return;
  Object.entries(draft).forEach(([key, value]) => localStorage.setItem(key, value));
};

/**
 * Drops any draft that does not belong to this user and this flow.
 *
 * The draft outlives the wizard being unmounted, so it also outlives two things it must not be
 * carried across. One person abandoning sign-up on a shared machine, where the next to sign in
 * would otherwise inherit their company details. And the wizard itself, which backs three
 * different jobs: a fresh sign-up, an admin adding a company, and editing an existing company's
 * additional information. A draft from one of those restored into another would submit against
 * the wrong company, and so would a sign-up draft restored for a different company than the
 * one it was started for.
 *
 * Called once when the wizard mounts, before its own state initialisers and before any child
 * reads a draft key of its own, so every restored field is covered by this one check.
 */
export const ensureOnboardingDraftScope = (userId: string | undefined, context: string): void => {
  // Defence in depth. The page gates the wizard on a hydrated session, so a missing user id here
  // should be unreachable; if it ever happens, leaving the draft alone beats discarding the one
  // the email-verification exchange just rescued.
  if (typeof window === 'undefined' || !userId) return;

  const scope = `${userId}:${context}`;
  const savedScope = localStorage.getItem(ONBOARDING_DRAFT_SCOPE_KEY);
  if (savedScope === scope) return;
  // A sign-up wizard that mounts before the new company has loaded doesn't know its company yet. That isn't a
  // different company, so the draft bound to the one just created stays.
  if (context === SIGN_UP_DRAFT_CONTEXT && savedScope?.startsWith(`${scope}:`)) return;
  // A sign-up draft saved before drafts were bound to a company has the bare scope. It's taken over by the company
  // being onboarded rather than dropped, which keeps what the user typed, as the wizard always has.
  if (context.startsWith(`${SIGN_UP_DRAFT_CONTEXT}:`) && savedScope === `${userId}:${SIGN_UP_DRAFT_CONTEXT}`) {
    localStorage.setItem(ONBOARDING_DRAFT_SCOPE_KEY, scope);
    return;
  }
  clearOnboardingDraft();
  localStorage.setItem(ONBOARDING_DRAFT_SCOPE_KEY, scope);
};

// Set when a user dismisses the top-bar referral banner (either via the close X or by clicking
// "Refer now"). Persists across logout so the banner stays dismissed for the lifetime of the browser.
export const REFERRAL_BANNER_DISMISSED_KEY = 'thera_referral_banner_dismissed';

export const isReferralBannerDismissed = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(REFERRAL_BANNER_DISMISSED_KEY) === 'true';
};

export const markReferralBannerDismissed = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(REFERRAL_BANNER_DISMISSED_KEY, 'true');
};
